import type {
  ConfigureFamily,
  ConfigureOptionDto,
  ConfigureOptionsResponse,
  ToolingGroupDto,
} from "@/api/configure-options";
import type { RegionCode } from "@/api/filter";
import { parseDecimalString } from "@/utils/cost-display";
import { isMspAutoFeedToggleSupported } from "@/utils/mspe-product";
import {
  catalogItemsForFamily,
  normUpper,
} from "@/utils/configure-catalog-display";
import {
  filterMdsCatalogItems,
  type MdsFilterState,
} from "@/utils/mds-configure";
import { CUSTOM_TOOLING_PART_NO } from "@/utils/configure-storage";

// =============================================================================
// configure-auto-select.ts — POC auto-select on modal open.
//
// D8b machine qty uses `requiredMachines` from comparison cost preview
// (same session as POC `ensureCostCalc` → `requiredMachines`).
// =============================================================================

export interface AutoSelectArgs {
  response: ConfigureOptionsResponse;
  family: ConfigureFamily;
  mspAutoFeedEnabled: boolean;
  productId: string;
  region: RegionCode;
  /** From comparison card cost preview (`summary.requiredMachines`). */
  requiredMachines: number;
  /**
   * MDS group/tracks/force filters in effect (user picks ?? server-inferred
   * defaults). Omitted -> fall back to `response.meta.mdsFilters`. Load-bearing
   * for MDS: the machine pick is the cheapest MACHINE row that survives these.
   */
  mdsFilters?: MdsFilterState;
  /**
   * Profil only — the element type the modal is currently showing. Auto-select
   * scales the catalog lines for this type; the server inferred `laneCount` +
   * `fastenerType` (meta.profilSelection) are valid for the inferred default
   * type, so scaling only runs when `profilElementType` matches it.
   */
  profilElementType?: string | null;
  existing: ReadonlyMap<string, number>;
}

export interface AutoSelectResult {
  selection: Map<string, number>;
  applied: string[];
}

export function applyAutoSelect(args: AutoSelectArgs): AutoSelectResult {
  const merged = new Map(args.existing);
  const applied: string[] = [];

  if (!args.response.available) {
    return { selection: merged, applied };
  }

  const reqMachines = Math.max(
    1,
    Math.floor(Number(args.requiredMachines) || 1),
  );

  switch (args.family) {
    case "sherex":
      applySherexAutoSelect(args.response, merged, applied);
      break;
    case "profil_indi":
      applyProfilAutoSelect(
        args.response,
        merged,
        applied,
        args.profilElementType ?? null,
      );
      break;
    case "t_series":
      applyTSeriesAutoSelect({
        response: args.response,
        merged,
        applied,
        region: args.region,
        requiredMachines: reqMachines,
      });
      break;
    case "heger_pem":
      applyHegerAutoSelect({
        response: args.response,
        merged,
        applied,
        requiredMachines: reqMachines,
        mspAutoFeedEnabled: args.mspAutoFeedEnabled,
        productId: args.productId,
        region: args.region,
      });
      break;
    case "mds":
      applyMdsAutoSelect({
        response: args.response,
        mdsFilters: args.mdsFilters ?? effectiveMdsFilters(args.response),
        merged,
        applied,
        requiredMachines: reqMachines,
      });
      break;
  }

  return { selection: merged, applied };
}

/** Server-inferred MDS defaults (meta.mdsFilters) as a plain filter state. */
function effectiveMdsFilters(
  response: ConfigureOptionsResponse,
): MdsFilterState {
  const m = response.meta.mdsFilters;
  return {
    fastenerGroup: m?.fastenerGroup ?? null,
    feederTracks: m?.feederTracks ?? null,
    forceKn: m?.forceKn ?? null,
  };
}

// ---------------------------------------------------------------------------
// MDS — POC `applyMdsAutoSelections`: cheapest MACHINE row (price ASC, tie by
// partNo) that survives the group/tracks/force filters, qty = requiredMachines.
// No option rows are auto-selected (the +180,000 robot adder is folded in by
// the configured-sum, not a catalog row). See gaps doc §2.4.
// ---------------------------------------------------------------------------

function applyMdsAutoSelect(args: {
  response: ConfigureOptionsResponse;
  mdsFilters: MdsFilterState;
  merged: Map<string, number>;
  applied: string[];
  requiredMachines: number;
}): void {
  const catalog = catalogItemsForFamily(args.response.catalogItems, "mds");
  const filtered = filterMdsCatalogItems(catalog, args.mdsFilters);
  const machineItems = filtered.filter(
    (o) => normUpper(o.subCategory) === "MACHINE",
  );

  // POC clears prior machine picks (filtered machine part-nos only) before
  // re-selecting.
  const machineKeys = new Set(machineItems.map((o) => o.partNo));
  for (const partNo of Array.from(args.merged.keys())) {
    if (machineKeys.has(partNo)) args.merged.delete(partNo);
  }
  if (machineItems.length === 0) return;

  const sorted = [...machineItems].sort((a, b) => {
    const pa = parseDecimalString(a.price);
    const pb = parseDecimalString(b.price);
    const fa = pa == null ? Number.POSITIVE_INFINITY : pa;
    const fb = pb == null ? Number.POSITIVE_INFINITY : pb;
    if (fa !== fb) return fa - fb;
    return a.partNo.localeCompare(b.partNo);
  });

  const picked = sorted[0]!;
  const qty = Math.max(1, Math.floor(args.requiredMachines) || 1);
  args.merged.set(picked.partNo, qty);
  args.applied.push(`mds.machine=${picked.partNo}×${qty}`);
}

// ---------------------------------------------------------------------------
// Heger — POC `applyHegerAutoSelections` (clears machine + tooling, re-picks).
// ---------------------------------------------------------------------------

/** POC `syncMspAutoFeedSelectionOnMap` — catalog upgrade rows only (D9). */
export function syncMspAutoFeedCatalogSelection(
  selection: ReadonlyMap<string, number>,
  catalogItems: ReadonlyArray<ConfigureOptionDto>,
  enabled: boolean,
  productId: string,
  region: RegionCode,
): Map<string, number> {
  if (!isMspAutoFeedToggleSupported(productId)) {
    return new Map(selection);
  }

  const next = new Map(selection);
  for (const item of findMspAutoFeedUpgradeItems(catalogItems, productId)) {
    next.delete(item.partNo);
  }

  if (enabled) {
    const preferred = findMspAutoFeedUpgrade(catalogItems, productId, region);
    if (preferred?.partNo) {
      next.set(preferred.partNo, 1);
    }
  }

  return next;
}

/**
 * POC `applyMspAutoFeedConfigSelection` when the configure modal is open:
 * strip tooling, re-run Heger auto-select (machine + tooling + catalog sync).
 */
export function syncMspAutoFeedSelection(args: {
  response: ConfigureOptionsResponse;
  selection: ReadonlyMap<string, number>;
  mspAutoFeedEnabled: boolean;
  productId: string;
  region: RegionCode;
  requiredMachines: number;
}): Map<string, number> {
  const catalog = catalogItemsForFamily(
    args.response.catalogItems,
    "heger_pem",
  );
  const next = syncMspAutoFeedCatalogSelection(
    args.selection,
    catalog,
    args.mspAutoFeedEnabled,
    args.productId,
    args.region,
  );

  // The toggle owns only the 50/60 row and the per-fastener manual/shuttle
  // recommendation. Preserve every unrelated user row and quantity, including
  // the configured machine quantity.
  for (const group of args.response.toolingGroups ?? []) {
    for (const item of group.items) next.delete(item.partNo);
    const pick = pickToolingForGroup(group, true, args.mspAutoFeedEnabled);
    if (pick) next.set(pick.partNo, 1);
  }
  return next;
}

/** Rows deliberately reconciled when the user changes the Auto-Feed toggle. */
export function mspAutoFeedManagedPartNos(
  response: ConfigureOptionsResponse,
  productId: string,
): Set<string> {
  const partNos = mspAutoFeedUpgradePartNos(response, productId);
  for (const group of response.toolingGroups ?? []) {
    for (const item of group.items) partNos.add(item.partNo);
  }
  return partNos;
}

/** Mutually-exclusive MAS-350 / eDrive 50 and 60 Hz catalog rows. */
export function mspAutoFeedUpgradePartNos(
  response: ConfigureOptionsResponse,
  productId: string,
): Set<string> {
  if (!isMspAutoFeedToggleSupported(productId)) return new Set();
  return new Set(
    findMspAutoFeedUpgradeItems(
      catalogItemsForFamily(response.catalogItems, "heger_pem"),
      productId,
    ).map((item) => item.partNo),
  );
}

function optionLabelText(o: ConfigureOptionDto): string {
  return `${o.description ?? ""} ${o.itemName ?? ""}`.toUpperCase();
}

function findMspAutoFeedUpgradeItems(
  items: ReadonlyArray<ConfigureOptionDto>,
  productId: string,
): ConfigureOptionDto[] {
  const id = normUpper(productId);
  const isEDrive = id.includes("EDRIVE");
  return items.filter((o) => {
    const desc = optionLabelText(o);
    return isEDrive
      ? /PRODUCTIVITY\s*PACK/i.test(desc)
      : /AUTOMATIC\s*FEED\s*UPGRADE/i.test(desc) && /MAS[\s-]*350/i.test(desc);
  });
}

function findMspAutoFeedUpgrade(
  items: ReadonlyArray<ConfigureOptionDto>,
  productId: string,
  region: RegionCode,
): ConfigureOptionDto | null {
  const matches = findMspAutoFeedUpgradeItems(items, productId);
  if (matches.length === 0) return null;

  const prefer60 = region === "NA";
  const preferred = matches.find((o) =>
    prefer60
      ? /60\s*HZ/i.test(String(o.description ?? ""))
      : /50\s*HZ/i.test(String(o.description ?? "")),
  );
  return preferred ?? matches[0] ?? null;
}

function applyHegerAutoSelect(args: {
  response: ConfigureOptionsResponse;
  merged: Map<string, number>;
  applied: string[];
  requiredMachines: number;
  mspAutoFeedEnabled: boolean;
  productId: string;
  region: RegionCode;
}): void {
  const catalog = catalogItemsForFamily(
    args.response.catalogItems,
    "heger_pem",
  );
  const machineOpts = catalog.filter(
    (o) =>
      normUpper(o.category) === "MACHINE" &&
      normUpper(o.subCategory) === "MACHINE",
  );
  const machineKeys = new Set(machineOpts.map((o) => o.partNo));
  const toolingPartNos = new Set<string>();
  for (const g of args.response.toolingGroups ?? []) {
    for (const o of g.items) toolingPartNos.add(o.partNo);
  }

  // POC: clear prior machine + tooling picks before auto-select.
  for (const partNo of Array.from(args.merged.keys())) {
    if (machineKeys.has(partNo) || toolingPartNos.has(partNo)) {
      args.merged.delete(partNo);
    }
  }

  const setQty = (partNo: string, qty: number) => {
    if (!partNo) return;
    args.merged.set(partNo, Math.max(1, Math.floor(qty) || 1));
  };

  if (catalog.some((o) => o.partNo === "INSTALL1")) {
    setQty("INSTALL1", 1);
    args.applied.push("heger.INSTALL1=1");
  }

  if (machineOpts.length > 0) {
    const sorted = [...machineOpts].sort((a, b) => {
      const pa = parseDecimalString(a.price) ?? -Infinity;
      const pb = parseDecimalString(b.price) ?? -Infinity;
      return pb - pa;
    });
    setQty(sorted[0]!.partNo, args.requiredMachines);
    args.applied.push(
      `heger.machine=${sorted[0]!.partNo}×${args.requiredMachines}`,
    );
  }

  const autoFeedAware = isMspAutoFeedToggleSupported(args.productId);
  const autoFeedEnabled = autoFeedAware ? args.mspAutoFeedEnabled : null;

  for (const group of args.response.toolingGroups ?? []) {
    const pick = pickToolingForGroup(group, autoFeedAware, autoFeedEnabled);
    if (pick) {
      setQty(pick.partNo, 1);
      args.applied.push(`heger.tooling.${group.fastenerPartNo}=${pick.partNo}`);
    }
  }

  if (autoFeedAware) {
    const synced = syncMspAutoFeedCatalogSelection(
      args.merged,
      catalog,
      args.mspAutoFeedEnabled,
      args.productId,
      args.region,
    );
    args.merged.clear();
    synced.forEach((qty, partNo) => args.merged.set(partNo, qty));
    const upgrade = findMspAutoFeedUpgrade(
      catalog,
      args.productId,
      args.region,
    );
    if (upgrade && args.mspAutoFeedEnabled) {
      args.applied.push(`heger.mspAutoFeedUpgrade=${upgrade.partNo}`);
    } else if (!args.mspAutoFeedEnabled) {
      args.applied.push("heger.mspAutoFeedUpgrade=cleared");
    }
  }
}

function pickToolingForGroup(
  group: ToolingGroupDto,
  autoFeedAware: boolean,
  autoFeedEnabled: boolean | null,
): ConfigureOptionDto | null {
  const opts = [...group.items];
  if (opts.length === 0) return null;

  const labelOf = (o: ConfigureOptionDto) =>
    `${o.partNo} ${o.itemName ?? ""} ${o.description ?? ""}`.toUpperCase();
  const toolingTypeOf = (o: ConfigureOptionDto) =>
    normUpper(o.subCategory ?? o.category ?? "");
  const isAbft = (o: ConfigureOptionDto) => labelOf(o).includes("ABFT");
  const isSet = (o: ConfigureOptionDto) => {
    const partNo = normUpper(o.partNo);
    return partNo.startsWith("SET") || labelOf(o).includes(" SET");
  };
  const isShuttle = (o: ConfigureOptionDto) =>
    toolingTypeOf(o).includes("SHUTTLE") || labelOf(o).includes("SHUTTLE");
  const isManual = (o: ConfigureOptionDto) =>
    toolingTypeOf(o).includes("MANUAL") || labelOf(o).includes("MANUAL");

  const manualOpts = opts.filter((o) => isManual(o) && !isAbft(o));
  const shuttleOpts = opts.filter((o) => isShuttle(o) && !isAbft(o));
  const setOpts = opts.filter((o) => isSet(o) && !isAbft(o));
  const nonAbftOpts = opts.filter((o) => !isAbft(o));

  let pickFrom: ConfigureOptionDto[];
  if (autoFeedAware) {
    pickFrom = autoFeedEnabled
      ? shuttleOpts.length
        ? shuttleOpts
        : setOpts.length
          ? setOpts
          : nonAbftOpts.length
            ? nonAbftOpts
            : opts
      : manualOpts.length
        ? manualOpts
        : setOpts.length
          ? setOpts
          : nonAbftOpts.length
            ? nonAbftOpts
            : opts;
  } else {
    pickFrom = shuttleOpts.length
      ? shuttleOpts
      : setOpts.length
        ? setOpts
        : nonAbftOpts.length
          ? nonAbftOpts
          : opts;
  }

  pickFrom.sort((a, b) => {
    const pa = parseDecimalString(a.price) ?? -Infinity;
    const pb = parseDecimalString(b.price) ?? -Infinity;
    return pb - pa;
  });
  return pickFrom[0] ?? null;
}

// ---------------------------------------------------------------------------
// Sherex — POC `applySherexAutoSelections`.
//
// Skip when the user already has a selection. Otherwise select EVERY catalog
// option (qty 1) whose thread variants intersect the apps' SHEREX-fastener
// target thread variants (server-inferred, meta.sherexTargetThreadVariants).
// The MVP previously picked the single globally-cheapest row — wrong line count
// and total vs POC's thread-matched set.
// ---------------------------------------------------------------------------
function applySherexAutoSelect(
  resp: ConfigureOptionsResponse,
  merged: Map<string, number>,
  applied: string[],
): void {
  if (merged.size > 0) return;
  const targets = new Set(resp.meta.sherexTargetThreadVariants ?? []);
  if (targets.size === 0) return;
  for (const item of resp.catalogItems) {
    const variants = item.sherexThreadVariants ?? [];
    if (variants.some((v) => targets.has(v))) {
      merged.set(item.partNo, 1);
      applied.push(`sherex.thread=${item.partNo}`);
    }
  }
}

// ---------------------------------------------------------------------------
// Profil InDie — POC `applyProfilIndiAutoSelections`.
//
// The element type, lane count and NUT/STUD majority are inferred server-side
// (the FE has no profil_fasteners master) and arrive in `meta.profilSelection`.
// Two branches mirror the POC exactly:
// - modern IN-DIE (NA): N-track feeders + per-fastener tooling, scaled by lanes
// - legacy (EU): Sorting/Feeding device + Track 3+4 / 5+6 + tooling, scaled by lanes
// Scaling only runs when the modal's active element type equals the inferred
// one (the server's laneCount/fastenerType are computed for that type).
// ---------------------------------------------------------------------------

interface ProfilTrackCandidate {
  item: ConfigureOptionDto;
  label: string;
  tracks: number;
}

/** POC `pickProfilTrackCombination` — greedily fill `lanes` from track sizes. */
function pickProfilTrackCombination(
  candidates: ReadonlyArray<ProfilTrackCandidate>,
  lanes: number,
): ProfilTrackCandidate[] {
  const out: ProfilTrackCandidate[] = [];
  const sorted = candidates
    .slice()
    .sort((a, b) => a.tracks - b.tracks || a.label.localeCompare(b.label));
  if (sorted.length === 0 || !Number.isFinite(lanes) || lanes <= 0) return out;
  const maxTracks = sorted[sorted.length - 1]!.tracks;
  let remaining = lanes;
  while (remaining > maxTracks) {
    const fit =
      sorted.filter((rec) => rec.tracks <= remaining).slice(-1)[0] ??
      sorted[sorted.length - 1]!;
    out.push(fit);
    remaining -= fit.tracks;
  }
  const finalPick =
    sorted.find((rec) => rec.tracks >= remaining) ?? sorted[sorted.length - 1]!;
  out.push(finalPick);
  return out;
}

function applyProfilAutoSelect(
  resp: ConfigureOptionsResponse,
  merged: Map<string, number>,
  applied: string[],
  activeElementType: string | null,
): void {
  const sel = resp.meta.profilSelection;
  if (!sel) return;
  // The server's laneCount/fastenerType are computed for the inferred type;
  // only scale when the modal is showing that type (on open it always is).
  if (activeElementType && activeElementType !== sel.elementType) return;
  const elementType = sel.elementType;
  const lanes = Math.max(1, Math.floor(sel.laneCount));
  if (lanes <= 0) return;

  const items = resp.catalogItems.filter((i) => i.elementType === elementType);
  if (items.length === 0) return;

  const byName = new Map<string, ConfigureOptionDto>();
  for (const o of items) {
    for (const key of [o.partNo, o.itemName]
      .map((v) => normUpper(v))
      .filter((v) => v.length > 0)) {
      if (!byName.has(key)) byName.set(key, o);
    }
  }
  const setQty = (item: ConfigureOptionDto, qty: number) => {
    if (!qty || qty <= 0) return;
    merged.set(item.partNo, qty);
    applied.push(`profil.${elementType}=${item.partNo}x${qty}`);
  };
  const setQtyByName = (name: string, qty: number) => {
    if (!qty || qty <= 0) return;
    const item = byName.get(normUpper(name));
    if (item) setQty(item, qty);
  };
  const itemLabel = (o: ConfigureOptionDto) =>
    String(o.itemName ?? o.partNo ?? "");
  const sub = (o: ConfigureOptionDto) => normUpper(o.subCategory ?? "");

  // Modern IN-DIE feeder candidates ("N Track Feeder … In-die").
  const feedTrackCandidates: ProfilTrackCandidate[] = [];
  for (const o of items) {
    if (sub(o) !== "FEEDER") continue;
    const label = itemLabel(o).trim();
    const m = label.match(/(\d+)\s*TRACK/i);
    const tracks = m ? Number(m[1]) : NaN;
    if (Number.isFinite(tracks) && /IN[- ]?DIE/i.test(label)) {
      feedTrackCandidates.push({ item: o, label, tracks });
    }
  }

  if (feedTrackCandidates.length > 0) {
    for (const rec of pickProfilTrackCombination(feedTrackCandidates, lanes)) {
      setQty(rec.item, (merged.get(rec.item.partNo) ?? 0) + 1);
    }
    for (const o of items) {
      const nm = itemLabel(o);
      const s = sub(o);
      if (s === "TOOLING" && /^CHUTING MOUNTING$/i.test(nm)) setQty(o, lanes);
      if (s === "TOOLING" && /^VACUUM FEED SUPPORT$/i.test(nm))
        setQty(o, lanes);
      if (/^CHUTING STUD\b/i.test(nm)) {
        if (s === "FEEDER") setQty(o, lanes * 5);
        if (s === "TOOLING") setQty(o, lanes);
      }
    }
    if (sel.fastenerType === "NUT") {
      for (const o of items) {
        const nm = itemLabel(o);
        const s = sub(o);
        if (s === "FEEDER" && /CHUTING NUT/i.test(nm)) setQty(o, lanes);
        if (s === "FEEDER" && /END OF CHUTING LOCKING DEVICE NUT/i.test(nm))
          setQty(o, lanes);
        if (s === "TOOLING" && /^PIERCE HEAD/i.test(nm)) setQty(o, lanes);
        if (s === "TOOLING" && /^COUPLING 1$/i.test(nm)) setQty(o, lanes);
        if (s === "TOOLING" && /^DIE BUTTON/i.test(nm)) setQty(o, lanes);
        if (s === "TOOLING" && /^BUTTON SHIM KIT$/i.test(nm)) setQty(o, lanes);
      }
    } else if (sel.fastenerType === "STUD") {
      for (const o of items) {
        const nm = itemLabel(o);
        const s = sub(o);
        if (s === "FEEDER" && /END OF CHUTING LOCKING DEVICE STUD/i.test(nm))
          setQty(o, lanes);
        if (s === "TOOLING" && /^PIERCE HEAD/i.test(nm)) setQty(o, lanes);
        if (s === "TOOLING" && /^COUPLING 2$/i.test(nm)) setQty(o, lanes);
        if (s === "TOOLING" && /^DIE BUTTON/i.test(nm)) setQty(o, lanes);
        if (s === "TOOLING" && /^BUTTON SHIM KIT$/i.test(nm)) setQty(o, lanes);
      }
    }
    for (const o of items) {
      if (sub(o) !== "TOOLING") continue;
      if (/^ELECTRO-PNUEMATIC$/i.test(itemLabel(o))) setQty(o, 1);
    }
    return;
  }

  // Legacy EU branch.
  const feeders = Math.max(1, Math.ceil(lanes / 6));
  let track34 = 0;
  let track56 = 0;
  let remaining = lanes;
  for (let i = 0; i < feeders; i += 1) {
    const use = Math.min(remaining, 6);
    if (use > 2) track34 += 1;
    if (use > 4) track56 += 1;
    remaining -= use;
  }
  setQtyByName("Sorting / Feeding Device", feeders);
  setQtyByName("Track 3 + 4", track34);
  setQtyByName("Track 5 + 6", track56);

  for (const o of items) {
    if (sub(o) === "FEEDER" && /COUPLING/i.test(itemLabel(o))) setQty(o, lanes);
  }
  setQtyByName("Feeding Tube", lanes);
  setQtyByName("Documentation", feeders);
  setQtyByName("Canister Oil", 1);

  for (const o of items) {
    if (sub(o) !== "TOOLING") continue;
    const up = normUpper(itemLabel(o));
    if (up.startsWith("COUPLING EXIT")) setQty(o, lanes);
    if (up === "COUPLING FEEDING TUBE") setQty(o, lanes);
    if (up === "FEEDING TUBE (IN DIE)") setQty(o, lanes);
    if (up === "DIE BUTTON") setQty(o, lanes);
    if (up === "PIERCEHEAD" || up === "PIERCEHEAD - LOCKING") setQty(o, lanes);
    if (up === "INSTALLATION MATERIAL") setQty(o, 1);
  }
}

// ---------------------------------------------------------------------------
// T-Series — POC `applyTSeriesAutoSelections`.
// Region-split, quantity-driven:
// appCount = scoped applications (meta.tSeriesCounts.appCount)
// typeCount = distinct brand+type, qty>0 (meta.tSeriesCounts.typeCount)
// feederCount= clamp(typeCount, 1, 8)
// reqMachines= summary.requiredMachines (cost preview)
// EU (EUR): fixed partNos (PAT-2400×req, T-Commissioning×req+1, T-Support×20,
// T2400Jig×appCount, MAS-350 & T12/2400Tooling×typeCount).
// NA/ASIA (USD): PAT2400 F{feederCount}×req, 21-day commissioning×1,
// auto-feed tooling×typeCount, panel jig×appCount (matched by description).
// ---------------------------------------------------------------------------

function applyTSeriesAutoSelect(args: {
  response: ConfigureOptionsResponse;
  merged: Map<string, number>;
  applied: string[];
  region: RegionCode;
  requiredMachines: number;
}): void {
  if (!args.response.available) return;
  const items = args.response.catalogItems.filter((o) => o.kind === "catalog");
  if (items.length === 0) return;

  const counts = args.response.meta.tSeriesCounts;
  const appCount = Math.max(0, Math.floor(counts?.appCount ?? 0));
  const typeCount = Math.max(0, Math.floor(counts?.typeCount ?? 0));
  const feederCount = Math.max(1, Math.min(8, typeCount || 1));
  const reqMachines = Math.max(1, Math.floor(args.requiredMachines) || 1);

  const setQty = (partNo: string | null | undefined, qty: number) => {
    if (!partNo) return;
    const q = Math.max(0, Math.floor(Number(qty) || 0));
    if (q <= 0) args.merged.delete(String(partNo));
    else args.merged.set(String(partNo), q);
  };

  const desc = (o: ConfigureOptionDto) => String(o.description ?? "");
  const isMachine = (o: ConfigureOptionDto) =>
    normUpper(o.category) === "MACHINE" &&
    normUpper(o.subCategory) === "MACHINE";
  const isService = (o: ConfigureOptionDto) =>
    normUpper(o.subCategory) === "SERVICE";
  const isMachineOption = (o: ConfigureOptionDto) =>
    normUpper(o.subCategory) === "MACHINE OPTIONS";

  const isUsd = args.region === "NA" || args.region === "ASIA";

  if (isUsd) {
    // Clear prior auto-feed picks before re-selecting (POC clearMatching).
    for (const o of items) {
      if (
        (isMachine(o) && /^PAT(?:600|1200|2400)\s*F\d+$/i.test(o.partNo)) ||
        (isService(o) && /21\s*day\s*commissioning/i.test(desc(o))) ||
        (isMachineOption(o) && /auto\s*feed\s*tooling/i.test(desc(o))) ||
        (isMachineOption(o) && /panel\s*jig/i.test(desc(o)))
      ) {
        args.merged.delete(o.partNo);
      }
    }
    if (!appCount || !typeCount) return;

    const machine = items.find(
      (o) => isMachine(o) && normUpper(o.partNo) === `PAT2400 F${feederCount}`,
    );
    const commissioning = items.find(
      (o) => isService(o) && /21\s*day\s*commissioning/i.test(desc(o)),
    );
    const tooling = items.find(
      (o) => isMachineOption(o) && /auto\s*feed\s*tooling/i.test(desc(o)),
    );
    const panelJig = items.find(
      (o) => isMachineOption(o) && /panel\s*jig/i.test(desc(o)),
    );

    setQty(machine?.partNo, reqMachines);
    setQty(commissioning?.partNo, 1);
    setQty(tooling?.partNo, typeCount);
    setQty(panelJig?.partNo, appCount);
    if (machine) {
      args.applied.push(`tseries.usd.machine=${machine.partNo}×${reqMachines}`);
    }
    return;
  }

  // EU (EUR) — fixed partNos. qty 0 clears (e.g. no apps -> all dropped).
  setQty("PAT-2400", appCount ? reqMachines : 0);
  setQty("PAT-600", 0);
  setQty("PAT-1200", 0);
  setQty("T-Commissioning", appCount ? reqMachines + 1 : 0);
  setQty("T-Support", appCount ? 20 : 0);
  setQty("T-Suopport", appCount ? 20 : 0);
  setQty("T2400Jig", appCount);
  setQty("MAS-350", typeCount);
  setQty("T12/2400Tooling", typeCount);
  args.applied.push(
    `tseries.eu.PAT-2400×${appCount ? reqMachines : 0},jig×${appCount},tooling×${typeCount}`,
  );
}

export function stripToolingSelections(
  selection: ReadonlyMap<string, number>,
  groups: ReadonlyArray<ToolingGroupDto>,
): Map<string, number> {
  const next = new Map(selection);
  for (const group of groups) {
    for (const item of group.items) {
      if (item.kind === "tooling") next.delete(item.partNo);
    }
  }
  next.delete(CUSTOM_TOOLING_PART_NO);
  return next;
}
